'use strict';

import JeefaceFilterApi from '../vendor/jeelizFaceFilter';
import THREE from './three';
import Detector from './detector';
import GifLoader from 'three-gif-loader';
import OBJLoader from '../vendor/obj-loader';

import { defaultVertexShader, defaultFragmentShader } from '../shaders/defaults';

const settings = {
  // negative -> look upper. in radians
  rotationOffsetX: 0.2,
  // in degrees, 3D camera FOV
  cameraFOV: 40,
  // XYZ of the distance between the center of the cube and the pivot
  pivotOffsetYZ: [0.1, 0.1],
  // sensibility, between 0 and 1. Less -> more sensitive
  detectionThreshold: 0.9,
  // buffer around threshold to prevent flapping
  detectionHysteresis: 0.02,
  // scale of the 3D cube
  scale: 1.2
};

const modelSettings = {
  scale: 0.7,
  rotation: [0, 0, 0],
  position: [0, 0.5, 0]
};

class FaceTracker {
  constructor(canvas, opts = {}) {
    this.canvas = canvas;
    this.opts = opts;

    this.loaded = false;
    this.maxFaces = opts.maxFaces || 1;
    // this.faceTextureSrc;
    this.faceTextures = [];

    const { detectionThreshold, detectionHysteresis } = settings;

    this.faces = [];

    for (let i = 0; i < this.maxFaces; i++) {
      this.faces.push({
        faceDetector: new Detector(detectionThreshold, false, detectionHysteresis),
        mouthDetector: new Detector(0.6, false, 0.005)
      });
    }
  }

  correctMeshSettings(mesh) {
    mesh.scale.multiplyScalar(0.025);
    mesh.rotation.set(0, 0, 0);
    mesh.position.set(0, 0.25, -0.6);
    mesh.scale.set(1.1, 1, 1);
    mesh.frustumCulled = false;
    mesh.side = THREE.DoubleSide;
    mesh.depthTest = false;
  }

  init() {
    return this.setupJeeliz()
      .then(spec => this.setupScene(spec, this.opts))
      .then(() => this);
  }

  setupJeeliz() {
    return new Promise((resolve, reject) => {
      const callbackTrack = this.track.bind(this);
      const callbackReady = (errCode, spec) => {
        if (errCode) {
          reject(errCode);
          return;
        }

        resolve(spec);
      };

      const { stabilizationSettings } = this;

      JeefaceFilterApi.init({
        canvas: this.canvas,
        NNCpath: './app/js/vendor/', // root of NNC.json file
        maxFacesDetected: this.maxFaces || 1,
        stabilizationSettings,
        callbackReady,
        callbackTrack,
        videoSettings: {
          flipX: this.opts.facingMode === 'user',
          facingMode: this.opts.facingMode
        }
      });
    });
  }

  async setupScene(spec, { trackedImages }) {
    // INIT THE THREE.JS context
    this.renderer = new THREE.WebGLRenderer({
      context: spec.GL,
      canvas: spec.canvasElement
    });

    // Allow us to render two different scenes (3d face, and 2d frame)
    this.renderer.autoClear = false;

    this.loadFace();

    this.loadTrackedImages(trackedImages);

    // CREATE THE SCENE : https://threejs.org/docs/#manual/en/introduction/Creating-a-scene
    this.scene = new THREE.Scene();

    /* Uncomment to add a 3d model to the face */
    this.model = await this.loadModel_new('./app/models/zero.obj');
    // this.facePivoted.add(this.model);

    this.faces.forEach(face => {
      face.object = this.face.clone();
      this.scene.add(face.object);
    });

    const videoMesh = this.loadVideo(spec, this.opts.fragmentShader, this.opts.vertexShader);
    this.scene.add(videoMesh);

    // CREATE LIGHT : https://threejs.org/docs/#api/en/lights/HemisphereLight
    const hemLight = new THREE.HemisphereLight(0xffffbb, 0x080820, 1.8);
    this.scene.add(hemLight);

    // CREATE THE CAMERA
    const aspectRatio = spec.canvasElement.width / spec.canvasElement.height;
    this.camera = new THREE.PerspectiveCamera(settings.cameraFOV, aspectRatio, 0.1, 100);

    this.frameScene = this.createFrameScene(this.opts.tags);
  }

  createFrameScene(tags) {
    const { width, height } = this.renderer.domElement;

    const dpr = window.devicePixelRatio;
    const camera = new THREE.OrthographicCamera(
      width / -2 / dpr,
      width / 2 / dpr,
      height / -2 / dpr,
      height / 2 / dpr,
      0.01,
      200000
    );

    // Not sure why these need to be set, but without them, the plane does not render. TODO:
    // get to the bottom of it with a cleaner solution
    camera.position.set(0, 0, -1);
    camera.rotation.set(-Math.PI, 0, Math.PI);

    const scene = new THREE.Scene();

    for (let tag of tags.reverse()) {
      if (tag.canvas) {
        tag.texture3d = new THREE.CanvasTexture(tag.canvas);
      } else {
        if (tag.imageSrc.match(/\.gif$/i)) {
          tag.texture3d = new GifLoader(this.loadingManager).load(tag.imageSrc);
        } else {
          tag.texture3d = new THREE.TextureLoader(this.loadingManager).load(tag.imageSrc);
        }
      }

      tag.texture3d.minFilter = THREE.LinearFilter;
      tag.texture3d.flipY = false;
      tag.texture3d.wrapS = THREE.RepeatWrapping;
      tag.texture3d.repeat.x = -1;

      const tagMaterial = new THREE.MeshBasicMaterial({
        map: tag.texture3d,
        transparent: true
      });
      const tagGeometry = new THREE.PlaneGeometry(tag.width, tag.height);
      tag.object3d = new THREE.Mesh(tagGeometry, tagMaterial);
      tag.object3d.position.x = (tag.left + tag.width / 2 - 207) * -1;
      tag.object3d.position.y = tag.top + tag.height / 2 - 311;

      scene.add(tag.object3d);
    }

    scene.camera = camera; // just so we don't have to return the camera as well

    return scene;
  }

  togglePause(pause) {
    JeefaceFilterApi.toggle_pause(pause);
  }

  toggleSlow(slow) {
    JeefaceFilterApi.toggle_slow(slow);
  }

  track(detectStates) {
    if (!this.loaded || !this.camera) {
      return;
    }

    if (!Array.isArray(detectStates)) {
      detectStates = [detectStates];
    }

    for (const [index, detectState] of detectStates.entries()) {
      const face = this.faces[index];

      face.faceDetector.match(detectState.detected, () => {
        this.opts.onFaceDetectionChange(face.faceDetector.matching, face.object);
      });

      if (face.faceDetector.matching) {
        // move the cube in order to fit the head
        const tanFOV = Math.tan((this.camera.aspect * this.camera.fov * Math.PI) / 360); // tan(FOV/2), in radians
        const W = detectState.s; // relative width of the detection window (1-> whole width of the detection window)
        const D = 1 / (2 * W * tanFOV); // distance between the front face of the cube and the camera

        // coords in 2D of the center of the detection window in the viewport :
        const xv = detectState.x;
        const yv = detectState.y;

        // coords in 3D of the center of the cube (in the view coordinates system)
        const z = -D - 1.25; // minus because view coordinate system Z goes backward. -0.5 because z is the coord of the center of the cube (not the front face)
        const x = xv * D * tanFOV;
        const y = (yv * D * tanFOV) / this.camera.aspect;

        const { pivotOffsetYZ, rotationOffsetX } = settings;

        // move and rotate the cube
        face.object.position.set(x, y + pivotOffsetYZ[0], z + pivotOffsetYZ[1]);
        face.object.rotation.set(
          detectState.rx + rotationOffsetX,
          detectState.ry,
          detectState.rz,
          'XYZ'
        );

        let mouthOpening = (detectState.expressions[0] - 0.2) * 5;
        mouthOpening = Math.min(Math.max(mouthOpening, 0), 1);

        // Initial code to carve out into its own class, commenting out below for now:
        // if (mouthOpening >= 0.5) {
        //   face.object.scale.setY(1. + mouthOpening * 0.2);
        // } else {
        //   face.object.scale.setY(1);
        // }

        face.mouthDetector.match(detectState.expressions[0], isDetected => {
          this.opts.onMouthDetectionChange(isDetected, this.planeForFace(face));
        });
      }
    }

    // reinitialize the state of THREE.JS because JEEFACEFILTER have changed stuffs
    this.renderer.state.reset();

    // // trigger the render of the THREE.JS SCENE
    this.renderer.render(this.scene, this.camera);

    // Render the frame on top
    this.renderer.render(this.frameScene, this.frameScene.camera);
  }

  planeForFace(face) {
    const pivoted = face.object.children.find(c => c.name === 'face-pivoted');
    return pivoted.children.find(c => c.name == 'face-plane');
  }

  loadFace() {
    const { pivotOffsetYZ, scale } = settings;

    // COMPOSITE OBJECT WHICH WILL FOLLOW THE HEAD
    // in fact we create 2 objects to be able to shift the pivot point
    this.face = new THREE.Object3D();
    this.face.visible = false;
    this.face.frustumCulled = false;
    this.facePivoted = new THREE.Object3D();
    this.facePivoted.name = 'face-pivoted';
    this.facePivoted.frustumCulled = false;
    // position uses X, Y and Z coordinates
    this.facePivoted.position.set(0, -pivotOffsetYZ[0], -pivotOffsetYZ[1]);
    this.facePivoted.scale.set(scale, scale, scale);
    this.face.add(this.facePivoted);

    // the plane used for 2D image use with face tracking. this will be attached to the pivot object
    this.plane = new THREE.Object3D();
    this.plane.frustumCulled = false;
    this.plane.position.set(0, -pivotOffsetYZ[0], -pivotOffsetYZ[1]);
    this.plane.scale.set(-scale, scale, scale);
    this.plane.name = 'face-plane';
    this.facePivoted.add(this.plane);

    // DO NOT EDIT THE FACE MODEL. THIS IS USED TO ALLOW US TO SHIFT THE PIVOT POINT
    // FACE

    // Create the JSONLoader for the face : https://threejs.org/docs/#api/en/loaders/BufferGeometryLoader
    // docs for gltf loader, which you may need to use for different projects
    // GLTF:https://threejs.org/docs/#examples/loaders/GLTFLoader
    const loader = new THREE.BufferGeometryLoader();
    loader.load('./app/models/face.json', geometry => {
      const material = new THREE.MeshBasicMaterial({});
      this.faceMesh = new THREE.Mesh(geometry, material);
      this.correctMeshSettings(this.faceMesh);
    });

    loader.load('./app/models/face.json', geometry => {
      let material = new THREE.MeshBasicMaterial({
        map: new THREE.TextureLoader().load(''),
        transparent: true
      });

      if (this.faceTextures.length > 0) {
        material.map = this.faceTextures[0].texture;
        material.opacity = this.faceTextures[0].opacity;
      }

      // Uncomment to see face model (for debugging)
      // material = new THREE.MeshNormalMaterial({ transparent: true, opacity: 0.8 });

      this.faceMesh = new THREE.Mesh(geometry, material);
      this.correctMeshSettings(this.faceMesh);

      this.facePivoted.add(this.faceMesh);

      for (let i = 1; i < this.faceTextures.length; ++i) {
        const newFace = this.faceMesh.clone();
        const newFaceMaterial = new THREE.MeshBasicMaterial({
          map: this.faceTextures[i].texture,
          transparent: true,
          opacity: this.faceTextures[i].opacity
        });
        newFace.material = newFaceMaterial;
        this.correctMeshSettings(newFace);
        this.facePivoted.add(newFace);
      }
    });
  }

  loadModel(modelPath, modelTexture) {
    return new Promise(resolve => {
      const loader = new OBJLoader(this.loadingManager);
      const group = new THREE.Group();

      const textureLoader = new THREE.TextureLoader(this.loadingManager);
      const material = new THREE.MeshStandardMaterial({
        map: textureLoader.load(modelTexture)
      });

      loader.load(modelPath, modelGroup => {
        modelGroup.traverse(function(child) {
          if (child instanceof THREE.Mesh) {
            child.material = material;
            child.castShadow = true;
            child.receiveShadow = true;
          }

          group.add(child);
          modelGroup.remove(child);
        });

        group.materialLibraries = modelGroup.materialLibraries;
        group.scale.multiplyScalar(modelSettings.scale);
        group.rotation.set(...modelSettings.rotation);
        group.position.set(...modelSettings.position);
        group.frustumCulled = false;

        resolve(group);
      });
    });
  }

  /*
   * TODO: This is a stopgap to do the least amount work possible to get the
   * face texture stuff to load — no sure what in this function is actually causing
   * texture/face model to load (they seem unrelated?) but will revisit this after
   * Think Summit 2019. - @cwervo
   */
  loadModel_new(modelPath) {
    return new Promise(resolve => {
      const loader = new OBJLoader(this.loadingManager);
      const group = new THREE.Group();
      loader.load(modelPath, () => {
        resolve(group);
      });
    });
  }

  get loadingManager() {
    if (this._loadingManager) {
      return this._loadingManager;
    }

    this._loadingManager = new THREE.LoadingManager();
    this._loadingManager.onError = url => console.error(`Error loading ${url}`);
    this._loadingManager.onLoad = () => (this.loaded = true);
    return this._loadingManager;
  }

  loadTrackedImages(images) {
    if (!images.length) {
      this.loaded = true;
      return;
    }

    for (let trackedImage of images) {
      if (trackedImage.element.classList.contains('face-texture')) {
        // We're using `unshift` below in order to respect Studio's layer order
        // Unshift prepends to the array, push would append, which would cause the "top"
        // layer in studio to be rendered last as a face texture. — @cwervo
        this.faceTextures.unshift({
          texture: new THREE.TextureLoader(this.loadingManager).load(trackedImage.imageUrl),
          opacity: trackedImage.opacity
        });
        // Don't load face textures as planes, just continue with the for loop
        continue;
      }
      const geometry = new THREE.PlaneGeometry(1, 1, 1, 1);

      let loader;
      if (trackedImage.imageUrl.match(/\.gif$/i)) {
        loader = new GifLoader(this.loadingManager);
      } else {
        loader = new THREE.TextureLoader(this.loadingManager);
      }

      const map = loader.load(trackedImage.imageUrl);
      const material = new THREE.MeshPhongMaterial({
        map,
        transparent: true,
        opacity: trackedImage.opacity,
        depthTest: false,
        side: THREE.DoubleSide
      });

      const planeImage = new THREE.Mesh(geometry, material);
      const isMouthOpenOnly = !!trackedImage.classNames.match(/mouth-open-only/);
      const isMouthClosedOnly = !!trackedImage.classNames.match(/mouth-closed-only/);
      planeImage.userData.mouthOpenOnly = isMouthOpenOnly;
      planeImage.userData.mouthClosedOnly = isMouthClosedOnly;

      planeImage.name = trackedImage.imageUrl;

      const positionMatch = trackedImage.classNames.match(/tap-to-change-(\d+)/);
      if (positionMatch) {
        const position = parseInt(positionMatch[1], 10);
        planeImage.userData.tapToChangePosition = position;
      }

      const scaleX = trackedImage.width / 250;
      const scaleY = trackedImage.height / 250;
      const centerX = trackedImage.left + Math.round(trackedImage.width / 2);
      const centerY = trackedImage.top + Math.round(trackedImage.height / 2);
      const xMult = 245;
      const yMult = 245;

      planeImage.frustumCulled = false;
      planeImage.side = THREE.DoubleSide;
      const newX = (215 - centerX) / xMult - 0.03;
      const newY = (300 - centerY) / yMult + 0.18;

      planeImage.rotateZ((trackedImage.transform || 0) * (Math.PI / 180));
      planeImage.rotateY(Math.PI);
      planeImage.position.set(newX, newY, 0.5);
      planeImage.scale.set(scaleX, scaleY, settings.scale);

      const { mouthOpenOnly, tapToChangePosition } = planeImage.userData;
      if (mouthOpenOnly || (tapToChangePosition && tapToChangePosition != 1)) {
        planeImage.visible = false;
      }

      this.plane.add(planeImage);
    }
  }

  loadVideo(spec, vertexShader = defaultVertexShader, fragmentShader = defaultFragmentShader) {
    spec.GL.getExtension('OES_standard_derivatives');

    //init video texture with red
    const videoTexture = new THREE.DataTexture(new Uint8Array([0, 0, 0]), 1, 1, THREE.RGBFormat);
    videoTexture.needsUpdate = true;
    videoTexture.wrapS = THREE.RepeatWrapping;
    videoTexture.repeat.x = -1;

    //CREATE THE VIDEO BACKGROUND
    const videoMaterial = new THREE.RawShaderMaterial({
      depthWrite: false,
      depthTest: false,
      vertexShader,
      fragmentShader,
      uniforms: {
        samplerVideo: { value: videoTexture }
      }
    });

    videoMaterial.extensions.derivatives = true;

    const videoGeometry = new THREE.BufferGeometry();
    const videoScreenCorners = new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]);

    videoGeometry.addAttribute('position', new THREE.BufferAttribute(videoScreenCorners, 2));
    videoGeometry.setIndex(new THREE.BufferAttribute(new Uint16Array([0, 1, 2, 0, 2, 3]), 1));

    const videoMesh = new THREE.Mesh(videoGeometry, videoMaterial);
    videoMesh.onAfterRender = () => {
      // replace videoTexture.__webglTexture by the real video texture
      this.renderer.properties.update(videoTexture, '__webglTexture', spec.videoTexture);
      delete videoMesh.onAfterRender;
    };
    videoMesh.renderOrder = -1000; // render first
    videoMesh.frustumCulled = false;

    return videoMesh;
  }
}

export default FaceTracker;
